import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../api/client';

export const AdminPaymentConfigPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState({
    payin: false,
    payout: false,
  });
  const [formData, setFormData] = useState({
    merchant_id: '',
    payin_api_key: '',
    payout_api_key: '',
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getPaymentConfig();
      if (response.data.data) {
        setFormData({
          merchant_id: response.data.data.merchant_id || '',
          payin_api_key: response.data.data.payin_api_key || '',
          payout_api_key: response.data.data.payout_api_key || '',
        });
      }
      setError('');
    } catch (err) {
      setError('Failed to load payment gateway configuration');
      console.error('Error fetching config:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!formData.merchant_id.trim() || !formData.payin_api_key.trim() || !formData.payout_api_key.trim()) {
      setError('All fields are required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await adminAPI.updatePaymentConfig({
        merchant_id: formData.merchant_id.trim(),
        payin_api_key: formData.payin_api_key.trim(),
        payout_api_key: formData.payout_api_key.trim(),
      });

      setSuccess('Payment gateway configuration saved successfully! ✅');
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      setError('Failed to save configuration. Please try again.');
      console.error('Error saving config:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Navigation */}
      <nav className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-blue-600">Payment Gateway Configuration</h1>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-600 hover:text-gray-800"
          >
            Back to Dashboard
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto py-8 px-4">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            ❌ {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="mb-8 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
            <h3 className="font-semibold text-blue-900 mb-2">📌 API Configuration Information</h3>
            <p className="text-sm text-blue-800">
              Enter your payment gateway API credentials here. These credentials will be used for all payment transactions including wallet top-ups and withdrawals.
            </p>
            <ul className="text-sm text-blue-800 mt-3 ml-4 list-disc space-y-1">
              <li><strong>Merchant ID:</strong> Your unique merchant identifier</li>
              <li><strong>Payin API Key:</strong> Used for receiving payments (wallet load transactions)</li>
              <li><strong>Payout API Key:</strong> Used for disbursing payments (withdrawal transactions)</li>
            </ul>
          </div>

          <form onSubmit={handleSave} className="space-y-6">
            {/* Merchant ID */}
            <div>
              <label htmlFor="merchant_id" className="block text-sm font-semibold text-gray-700 mb-2">
                🏪 Merchant ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="merchant_id"
                name="merchant_id"
                value={formData.merchant_id}
                onChange={handleChange}
                placeholder="Enter your merchant ID"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Your unique merchant identifier</p>
            </div>

            {/* Payin API Key */}
            <div>
              <label htmlFor="payin_api_key" className="block text-sm font-semibold text-gray-700 mb-2">
                💳 Payin API Key (Payment Collection) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword.payin ? 'text' : 'password'}
                  id="payin_api_key"
                  name="payin_api_key"
                  value={formData.payin_api_key}
                  onChange={handleChange}
                  placeholder="Enter your payin API key"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => ({ ...prev, payin: !prev.payin }))}
                  className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
                >
                  {showPassword.payin ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Used for wallet top-up and payment collection</p>
            </div>

            {/* Payout API Key */}
            <div>
              <label htmlFor="payout_api_key" className="block text-sm font-semibold text-gray-700 mb-2">
                🏦 Payout API Key (Payment Disbursement) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword.payout ? 'text' : 'password'}
                  id="payout_api_key"
                  name="payout_api_key"
                  value={formData.payout_api_key}
                  onChange={handleChange}
                  placeholder="Enter your payout API key"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(prev => ({ ...prev, payout: !prev.payout }))}
                  className="absolute right-3 top-3 text-gray-500 hover:text-gray-700"
                >
                  {showPassword.payout ? '👁️' : '👁️‍🗨️'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Used for withdrawal processing and fund disbursement</p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={saving || loading}
                className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? '💾 Saving...' : '💾 Save Configuration'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/admin')}
                className="flex-1 px-6 py-3 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>

          {/* Security Notice */}
          <div className="mt-8 p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded">
            <h3 className="font-semibold text-yellow-900 mb-2">🔒 Security Notice</h3>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• API keys are encrypted and stored securely on the server</li>
              <li>• Never share your API keys with anyone</li>
              <li>• Update your keys immediately if you suspect a compromise</li>
              <li>• All configuration changes are logged for audit purposes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPaymentConfigPage;
